/**
 * Learning Loop dashboard store (PROD-592).
 *
 * Holds the last-fetched `MetricsSnapshot` plus the configurable
 * refresh interval. The Learning Loop view subscribes to the store,
 * triggers a refresh on mount, and re-triggers on every interval
 * tick. Per HC1 two-mode operation, the store reads
 * `useModeStore.mode` at refresh time and dispatches to the Demo or
 * App Insights client accordingly. Live falls back to Demo when the
 * App Insights SDK is not bundled (INFRA GATE deferred per
 * PROD-593).
 *
 * Public surface:
 *   - `snapshot: MetricsSnapshot | null` - latest fetched snapshot;
 *     null on first paint before the refresh resolves.
 *   - `lastFetchedAt: number | null` - ms epoch of the last refresh.
 *   - `refreshInterval: number` - ms between automatic refreshes;
 *     defaults to `DEFAULT_REFRESH_INTERVAL_MS` per the ticket.
 *   - `refresh(now)` - fetch a fresh snapshot. The caller passes a
 *     `now` value so test setups can pin a deterministic
 *     `lastFetchedAt`.
 *   - `setRefreshInterval(ms)` - update the interval. The view
 *     reschedules its timer.
 *   - `setMetricsClient(factory)` - dependency injection seam for
 *     tests + Live Mode wiring.
 *
 * HC9 telemetry-real: this store does NOT emit telemetry directly;
 * the view emits `learning_loop_view` on mount and on every tick.
 *
 * HC5: zero em / en dashes anywhere.
 */
import { create } from 'zustand';
import type { MetricsClient } from '../services/metrics/MetricsClient';
import {
  DEFAULT_REFRESH_INTERVAL_MS,
  type MetricsSnapshot,
  type TimeWindow,
} from '../services/metrics/types';
import { createDemoMetricsClient } from '../services/metrics/DemoMetricsClient';
import { createAppInsightsMetricsClient } from '../services/metrics/AppInsightsMetricsClient';
import { useModeStore } from './mode.store';
import { FALLBACK_RUNTIME_CONFIG, type RuntimeConfig } from './runtime-config';

/** Factory shape for the metrics client. Mode-aware. */
export type MetricsClientFactory = (mode: 'demo' | 'live') => MetricsClient;

/**
 * Hydration seam: production reads runtime config in `main.tsx` and
 * forwards the App Insights connection string + workspace id here so
 * Live mode boots with real credentials. The default mirrors
 * `FALLBACK_RUNTIME_CONFIG` (both fields null) so dev / preview /
 * Demo Mode boots cleanly without secrets wired.
 */
let runtimeConfigSlice: Pick<
  RuntimeConfig,
  'appInsightsConnectionString' | 'appInsightsWorkspaceId'
> = {
  appInsightsConnectionString: FALLBACK_RUNTIME_CONFIG.appInsightsConnectionString,
  appInsightsWorkspaceId: FALLBACK_RUNTIME_CONFIG.appInsightsWorkspaceId,
};

/**
 * Set the App Insights config used by the default metrics-client
 * factory. Called once at app boot from `main.tsx` after the runtime
 * config resolves. Tests inject their own factory via
 * `setMetricsClientFactory` and do not touch this seam.
 */
export function setLearningLoopRuntimeConfig(
  cfg: Pick<
    RuntimeConfig,
    'appInsightsConnectionString' | 'appInsightsWorkspaceId'
  >,
): void {
  runtimeConfigSlice = {
    appInsightsConnectionString: cfg.appInsightsConnectionString,
    appInsightsWorkspaceId: cfg.appInsightsWorkspaceId,
  };
}

/**
 * Default factory. Demo mode resolves to `createDemoMetricsClient`;
 * Live mode resolves to `createAppInsightsMetricsClient` configured
 * with the runtime-config connection string + workspace id. The Live
 * client surfaces `null` from `snapshot` when the SDK is not bundled
 * or auth fails; the store catches that and falls back to Demo so the
 * dashboard always renders.
 */
export const defaultMetricsClientFactory: MetricsClientFactory = (mode) => {
  if (mode === 'live') {
    return createAppInsightsMetricsClient({
      connectionString: runtimeConfigSlice.appInsightsConnectionString ?? '',
      workspaceId: runtimeConfigSlice.appInsightsWorkspaceId,
    });
  }
  return createDemoMetricsClient();
};

export interface LearningLoopState {
  /** Latest snapshot; null on first paint before any fetch lands. */
  snapshot: MetricsSnapshot | null;
  /** Epoch ms of the last successful refresh. Null before first fetch. */
  lastFetchedAt: number | null;
  /** Configurable refresh interval in ms; default 60_000. */
  refreshInterval: number;
  /**
   * Most recent error message from a failed refresh attempt. Null in
   * the happy path. Surfaced so the view can render an inline retry
   * affordance.
   */
  refreshError: string | null;
  /** Mode the most recent snapshot was fetched against. */
  lastMode: 'demo' | 'live' | null;
  /** Replace the metrics client factory. Used by tests + Live wiring. */
  setMetricsClientFactory: (factory: MetricsClientFactory) => void;
  /** Update the auto-refresh interval. */
  setRefreshInterval: (ms: number) => void;
  /**
   * Fetch a fresh snapshot. Reads `useModeStore.mode` at call time so
   * a mode toggle between ticks is honored. Returns the snapshot for
   * tests; the view consumes via the store subscription.
   */
  refresh: (now?: number, window?: TimeWindow) => Promise<MetricsSnapshot | null>;
  /** Reset to first-paint state. Tests + Reset Demo. */
  reset: () => void;
}

const INITIAL_STATE: Omit<
  LearningLoopState,
  | 'setMetricsClientFactory'
  | 'setRefreshInterval'
  | 'refresh'
  | 'reset'
> = {
  snapshot: null,
  lastFetchedAt: null,
  refreshInterval: DEFAULT_REFRESH_INTERVAL_MS,
  refreshError: null,
  lastMode: null,
};

let activeFactory: MetricsClientFactory = defaultMetricsClientFactory;

export const useLearningLoopStore = create<LearningLoopState>((set) => ({
  ...INITIAL_STATE,
  setMetricsClientFactory: (factory) => {
    activeFactory = factory;
  },
  setRefreshInterval: (ms) => {
    if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) {
      console.warn(
        `[learning-loop.store] setRefreshInterval rejected non-positive value ${String(ms)}`,
      );
      return;
    }
    set({ refreshInterval: ms });
  },
  refresh: async (now, window) => {
    const mode = useModeStore.getState().mode;
    const client = activeFactory(mode);
    try {
      let snap = await client.snapshot(window);
      // Live client returns null when the SDK is not bundled; fall
      // back to Demo so the dashboard always renders with content.
      if (snap === null && mode === 'live') {
        const fallback = createDemoMetricsClient();
        snap = await fallback.snapshot(window);
      }
      const ts = typeof now === 'number' ? now : Date.now();
      set({
        snapshot: snap,
        lastFetchedAt: ts,
        refreshError: null,
        lastMode: mode,
      });
      return snap;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Refresh failed';
      console.warn('[learning-loop.store] refresh failed', err);
      set({ refreshError: message });
      return null;
    }
  },
  reset: () => {
    set({ ...INITIAL_STATE });
  },
}));
